import { FormControl, FormErrorMessage, FormHelperText, InputGroup, InputRightAddon, Spinner } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SourcingEventDto, SourcingEventPatchDto } from "../../../../../autogen/bff-api";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { FormLabelWithInfo } from "../../../../../common/input/FormLabelWithInfo";
import { WholeNumberInput } from "../../../../../common/input/WholeNumberInput";

const FIELD = "publicContext.eFormsNoticeData.tenderValidityMonths";

interface Props {
  eventId: string;
  months: number | null;
  isEditable: boolean;
}

export const TenderValidityField = ({ eventId, months, isEditable }: Props) => {
  const { t } = useTranslation();
  const { updateField, fieldStatus, fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const [input, setInput] = useState<number | null>(months);
  const notWhole = Number.isNaN(input);
  const error = fieldError(FIELD);
  const saving = fieldStatus(FIELD) === "saving" || fieldStatus(FIELD) === "pending";
  // t('month_one') t('month_other')
  const unit = t("month", { count: notWhole ? 1 : (input ?? 1) });

  return (
    <FormControl mt="4" isInvalid={notWhole || !!error} isDisabled={!isEditable}>
      <FormLabelWithInfo
        tooltip={t("Specifies how long suppliers are bound by their bid. Common practice is 3 or 6 months.")}
        suffix={saving && <Spinner size="xs" ml="1" />}
      >
        {t("Tender validity period")}
      </FormLabelWithInfo>
      <InputGroup maxW="250px">
        <WholeNumberInput
          numericValue={input}
          maxLength={3}
          borderRightRadius="0"
          onNumericChange={(value) => {
            setInput(value);
            if (!Number.isNaN(value)) {
              updateField({ publicContext: { eFormsNoticeData: { tenderValidityMonths: value } } }, { debounceMs: 300 });
            }
          }}
        />
        <InputRightAddon color="smMuted" fontSize="sm">
          {unit}
        </InputRightAddon>
      </InputGroup>
      {notWhole ? (
        <FormErrorMessage>{t("Enter a whole number from 1 to 999")}</FormErrorMessage>
      ) : error ? (
        <FormErrorMessage>{formatFieldError(error)}</FormErrorMessage>
      ) : (
        <FormHelperText>{t("Counted from the bid deadline.")}</FormHelperText>
      )}
    </FormControl>
  );
};
